import React, { useState, useEffect, useRef } from "react";
import { Download } from "lucide-react";
import Mech from "../images/mech.jpg";
import Electrical from "../images/ee.jpg";
import IT from "../images/it.jpg";
import ECADModules from "./ECADModules";
import MechanicalModules from "./MechanicalModules";
import ITModules from "./ITModules";

const branches = [
  {
    title: "Mechanical Engineering",
    image: Mech,
    brochure: "/brochures/mechanical.pdf",
    description:
      "Covers 3D design, manufacturing processes, and panel drafting for real-world mechanical applications.",
    content: <MechanicalModules />,
  },
  {
    title: "Electronic & Electrical Engineering",
    image: Electrical,
    brochure: "/brochures/electrical.pdf",
    description:
      "Focuses on circuit design, industrial automation, and electrical drafting using ECAD tools.",
    content: <ECADModules />,
  },
  {
    title: "Information Technology (IT)",
    image: IT,
    brochure: "/brochures/it.pdf",
    description:
      "Includes web development, backend programming, and deployment with practical project-based training.",
    content: <ITModules />,
  },
];

const InternshipBranchCards = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const popupRef = useRef(null);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const togglePopup = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setActiveIndex(null);
    }
  };

  useEffect(() => {
    if (!isMobile && activeIndex !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeIndex, isMobile]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 mb-4 ">
      {/* Cards Grid */}
      <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
        {branches.map((branch, index) => (
          <div key={index} className="w-full max-w-sm">
            <div
              className="relative bg-white rounded-xl shadow-md hover:shadow-xl overflow-hidden transition cursor-pointer group"
              onClick={() => togglePopup(index)}
            >
              {/* Hover Tooltip */}
              <div className="absolute top-2 left-2 bg-sky-600 text-white text-xs px-3 py-1 rounded shadow opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                Click to view details
              </div>

              {/* Download Button */}
              <a
                href={branch.brochure}
                download
                onClick={(e) => e.stopPropagation()}
                className="absolute top-3 right-3 bg-sky-950 hover:bg-sky-700 text-white w-10 h-10 flex items-center justify-center rounded-full shadow transition"
                title="Download Brochure"
              >
                <Download className="w-5 h-5" />
              </a>

              {/* Card Content */}
              <img
                src={branch.image}
                alt={branch.title}
                className="h-48 object-cover w-full"
              />
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {branch.title}
                </h3>
                <p className="text-gray-600 text-sm">{branch.description}</p>
              </div>
            </div>

            {/* MOBILE: Inline Popup */}
            {isMobile && activeIndex === index && (
              <div
                ref={popupRef}
                className="w-full bg-gray-50 border-t border-sky-300 rounded-xl shadow-inner px-4 py-4 mt-4 animate-fade-in"
              >
                {branch.content}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* DESKTOP: Full-width Popup */}
      {!isMobile && activeIndex !== null && (
        <div
          ref={popupRef}
          className="w-full bg-gray-50 border-t border-sky-300 rounded-xl shadow-inner px-6 py-6 mt-10 animate-fade-in"
        >
          {branches[activeIndex].content}
        </div>
      )}
    </div>
  );
};

export default InternshipBranchCards;
